import { useState } from "react"
import { PRODUCT_VISUAL_KINDS } from "@nova-ai/product-visual-templates/kinds"
import {
  type ProductConfigVisuals,
  type ProductVisualSimple,
  type ProductVisualStudioCard,
  type StoreCode,
} from "@nova-ai/shared"

import { useProductVisualRender } from "../../hooks/use-product-visual-render"
import { useProductVisualRegeneration } from "../../services/use-product-visual-regeneration"
import { QueryState } from "../layout/query-state"
import { ProductStoreSelect } from "../products/product-store-select"
import { Callout } from "../ui/callout"
import { ProductVisualCellCard } from "./product-visual-cell-card"
import { productVisualGapMessage } from "./product-visual-gap-message"
import {
  PRODUCT_VISUAL_KIND_ALL,
  ProductVisualKindFilterControl,
  type ProductVisualKindFilter,
} from "./product-visual-kind-control"
import { ProductVisualLightbox, type EnlargedProductVisual } from "./product-visual-lightbox"
import { ProductVisualReviewDetails } from "./product-visual-review-details"
import { ProductVisualDirectionControl } from "./product-visual-direction-control"
import { ProductPhotosSection } from "./product-photos-section"

type ProductVisualGalleryProps = {
  configSku: string
  store: StoreCode
  visualKind: ProductVisualKindFilter
  data: ProductConfigVisuals | undefined
  isLoading: boolean
  error: unknown
  onStoreChange: (store: StoreCode) => void
  onVisualKindChange: (kind: ProductVisualKindFilter) => void
}

// The product page's Visuals view (issue #251): every simple of this config in
// one store, with a card per visual kind. The operator never types a SKU — the
// page already knows the product, so Render acts on the cell in front of them.
export function ProductVisualGallery({
  configSku,
  store,
  visualKind,
  data,
  isLoading,
  error,
  onStoreChange,
  onVisualKindChange,
}: ProductVisualGalleryProps) {
  const [selected, setSelected] = useState<{
    item: ProductVisualStudioCard
    image: EnlargedProductVisual
    visualKind: (typeof PRODUCT_VISUAL_KINDS)[number]
  } | null>(null)
  const render = useProductVisualRender(configSku, store)
  const regeneration = useProductVisualRegeneration({ configSku, store })

  return (
    <section className="space-y-4">
      <ProductVisualDirectionControl configSku={configSku} />

      <ProductPhotosSection configSku={configSku} store={store} />

      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-foreground">Visuals</h2>

          <p className="mt-1 text-xs text-muted-foreground">{summaryLine(store, data)}</p>

          {/* Stated once for the whole grid rather than on every cell. Both halves
              are load-bearing: Render is not a read (it re-resolves sources and
              saves the canonical content behind the cell), and it does not run
              in parallel however many cells the operator clicks. */}
          <p className="mt-2 max-w-2xl text-xs text-muted-foreground">
            Render re-fills this cell&apos;s canonical content from its sources, then returns a
            preview image that is never saved. Renders run one at a time — the renderer is a single
            shared worker, so extra clicks queue.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <ProductVisualKindFilterControl value={visualKind} onChange={onVisualKindChange} />

          <ProductStoreSelect value={store} onChange={onStoreChange} className="w-40" />
        </div>
      </header>

      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={data?.simples.length === 0}
        emptyMessage={`This product has no simples mirrored in ${store}. Pick another store.`}
      >
        <ul className="space-y-6">
          {(data?.simples ?? []).map((simple) => (
            <li key={simple.simpleSku}>
              <SimpleVisuals
                simple={simple}
                configSku={configSku}
                visualKind={visualKind}
                render={render}
                regeneration={regeneration}
                onReview={(item, image, kind) => setSelected({ item, image, visualKind: kind })}
              />
            </li>
          ))}
        </ul>
      </QueryState>

      {selected ? (
        <ProductVisualLightbox
          image={selected.image}
          details={
            <ProductVisualReviewDetails
              image={selected.item}
              store={store}
              visualKind={selected.visualKind}
            />
          }
          onClose={() => setSelected(null)}
        />
      ) : null}
    </section>
  )
}

function SimpleVisuals({
  simple,
  configSku,
  visualKind,
  render,
  regeneration,
  onReview,
}: {
  simple: ProductVisualSimple
  configSku: string
  visualKind: ProductVisualKindFilter
  render: ReturnType<typeof useProductVisualRender>
  regeneration: ReturnType<typeof useProductVisualRegeneration>
  onReview: (
    item: ProductVisualStudioCard,
    image: EnlargedProductVisual,
    kind: (typeof PRODUCT_VISUAL_KINDS)[number]
  ) => void
}) {
  // Iterate the template registry, not the response order: the grid then reads
  // in one fixed kind order everywhere, and each cell's kind stays typed.
  const shownKinds = visualKind === PRODUCT_VISUAL_KIND_ALL ? PRODUCT_VISUAL_KINDS : [visualKind]

  return (
    <>
      <h3 className="mb-2 flex flex-wrap items-baseline gap-2">
        <span className="text-sm font-medium text-foreground">{variantLabel(simple)}</span>

        <span className="font-mono text-xs text-muted-foreground">{simple.simpleSku}</span>
      </h3>

      {simple.targetGap ? (
        <Callout tone="warning" title="Not a render target" dense>
          <span className="text-sm text-foreground/80">
            {productVisualGapMessage(simple.targetGap)} No visual kind renders for this simple.
          </span>
        </Callout>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {shownKinds.map((kind) => {
            const cell = simple.kinds.find((entry) => entry.visualKind === kind)
            if (!cell) {
              return null
            }
            const input = { simpleSku: simple.simpleSku, visualKind: kind }
            const cellRender = render.stateFor(input)
            const isRegenerating =
              regeneration.isPending &&
              regeneration.variables?.simpleSku === simple.simpleSku &&
              regeneration.variables.kind === kind
            return (
              <ProductVisualCellCard
                key={kind}
                simpleSku={simple.simpleSku}
                visualKind={kind}
                cell={cell}
                preview={cellRender.preview}
                renderStatus={cellRender.status}
                renderError={cellRender.error}
                onRender={() => render.renderCell(input)}
                regenerationStatus={isRegenerating ? "pending" : "idle"}
                regenerationError={
                  regeneration.isError &&
                  regeneration.variables?.simpleSku === simple.simpleSku &&
                  regeneration.variables.kind === kind
                    ? regeneration.error.message
                    : null
                }
                onRegenerate={() => regeneration.mutate({ simpleSku: simple.simpleSku, kind })}
                onEnlarge={(image) =>
                  onReview(
                    {
                      simpleSku: simple.simpleSku,
                      configSku,
                      productTitle: variantLabel(simple),
                      cell,
                    },
                    image,
                    kind
                  )
                }
              />
            )
          })}
        </div>
      )}
    </>
  )
}

/** Flavor and size are the axes an operator recognizes a variant by; a simple
 *  with neither falls back to its SKU, which the line already carries. */
function variantLabel(simple: ProductVisualSimple): string {
  const axes = [simple.flavor, simple.size].filter((axis): axis is string => Boolean(axis?.trim()))
  return axes.length > 0 ? axes.join(" · ") : "Variant"
}

function summaryLine(store: string, data: ProductConfigVisuals | undefined): string {
  if (!data) {
    return store
  }
  const shown = data.simples.length
  const capped = shown < data.total ? ` of ${data.total}` : ""
  const simpleSummary = `${store} · ${data.locale} · ${shown}${capped} simple${shown === 1 ? "" : "s"}`
  const nutrition = data.nutritionCoverage
  if (!nutrition) {
    return simpleSummary
  }
  return `${simpleSummary} · Nutrition values: ${nutrition.withValues} of ${nutrition.variants} variants`
}
